import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import AttributeModal from '../../components/admin/AttributeModal';
import styles from './AdminAttributes.module.css';

const AdminAttributes = () => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Cargar atributos
  const fetchAttributes = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.getAllProductAttributes();
      setAttributes(data.attributes || []);
    } catch (err) {
      setError('Error al cargar los atributos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttributes();
  }, []);

  // Limpiar mensajes de éxito
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Abrir modal para crear
  const handleCreateAttribute = () => {
    setModalMode('create');
    setSelectedAttribute(null);
    setModalOpen(true);
  };

  // Abrir modal para editar
  const handleEditAttribute = async (attribute) => {
    setModalMode('edit');
    
    // Si es variante, cargar sus opciones
    let attributeWithOptions = { ...attribute };
    if (attribute.category === 'variant') {
      try {
        const optionsData = await apiService.getAttributeOptions(attribute.id);
        attributeWithOptions.options = optionsData.options || [];
      } catch (err) {
        console.warn('Error al cargar opciones:', err);
        attributeWithOptions.options = [];
      }
    }
    
    setSelectedAttribute(attributeWithOptions);
    setModalOpen(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAttribute(null);
  };

  // Enviar formulario (crear o editar)
  const handleSubmitForm = async (formData) => {
    setLoading(true);
    setError('');
    try {
      if (modalMode === 'create') {
        // Extraer opciones si es variante
        const { options, ...attributeData } = formData;
        
        // 1. Crear el atributo principal
        const response = await apiService.createProductAttribute(null, [attributeData]);
        const newAttributeId = response.attribute?.id;

        // 2. Si es variante y tiene opciones, guardar las opciones
        if (attributeData.category === 'variant' && options && options.length > 0 && newAttributeId) {
          try {
            for (let i = 0; i < options.length; i++) {
              const option = options[i];
              if (option.value) { // Solo si tiene valor
                await apiService.createAttributeOption(
                  newAttributeId,
                  option.value,
                  option.display_name || option.value,
                  option.color_hex || null,
                  null,
                  i
                );
              }
            }
          } catch (optionErr) {
            console.warn('Error al guardar algunas opciones:', optionErr);
            // No frenamos el flujo si hay error en opciones
          }
        }

        setSuccess('Atributo creado exitosamente');
      } else {
        // Editar atributo existente
        const { options, ...attributeData } = formData;
        await apiService.updateProductAttribute(selectedAttribute.id, attributeData);

        // Si es variante, actualizar opciones
        if (attributeData.category === 'variant' && options && options.length > 0) {
          try {

            // Crear nuevas opciones que no existan
            for (let i = 0; i < options.length; i++) {
              const option = options[i];
              if (option.value && !option.id) { // Si no tiene ID, es nueva
                await apiService.createAttributeOption(
                  selectedAttribute.id,
                  option.value,
                  option.display_name || option.value,
                  option.color_hex || null,
                  null,
                  i
                );
              }
            }
          } catch (optionErr) {
            console.warn('Error al actualizar opciones:', optionErr);
          }
        }

        setSuccess('Atributo actualizado exitosamente');
      }
      handleCloseModal();
      await fetchAttributes();
    } catch (err) {
      setError(err.message || 'Error al guardar el atributo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Eliminar atributo
  const handleDeleteAttribute = async (id) => {
    if (!window.confirm('¿Estás seguro que deseas eliminar este atributo? Esto afectará todas las opciones asociadas.')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await apiService.deleteProductAttribute(id);
      setSuccess('Atributo eliminado exitosamente');
      await fetchAttributes();
    } catch (err) {
      setError(err.message || 'Error al eliminar el atributo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      text: 'Texto',
      color: 'Color',
      number: 'Número',
      boolean: 'Sí/No'
    };
    return labels[type] || type;
  };

  return (
    <AdminLayout>
      <div className={styles.attributeManagement}>
        <div className={styles.header}>
          <h1>Gestión de Atributos de Producto</h1>
          <button 
            className={styles.addButton} 
            onClick={handleCreateAttribute}
            disabled={loading}
          >
            ➕ Crear Atributo
          </button>
        </div>

        {error && (
          <div className={styles.alert + ' ' + styles.alertError}>
            <span>{error}</span>
            <button onClick={() => setError('')}>✕</button>
          </div>
        )}

        {success && (
          <div className={styles.alert + ' ' + styles.alertSuccess}>
            <span>{success}</span>
            <button onClick={() => setSuccess('')}>✕</button>
          </div>
        )}

        {loading && attributes.length === 0 ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Cargando atributos...</p>
          </div>
        ) : (
          <div className={styles.attributesTable}>
            <div className={styles.tableHeader}>
              <div className={styles.colName}>Nombre</div>
              <div className={styles.colType}>Tipo</div>
              <div className={styles.colCategory}>Categoría</div>
              <div className={styles.colActions}>Acciones</div>
            </div>

            {attributes.length === 0 ? (
              <div className={styles.emptyState}>
                <p>📭 No hay atributos creados aún</p>
                <p className={styles.emptyStateHint}>Crea tu primer atributo para empezar</p>
              </div>
            ) : (
              attributes.map((attribute) => (
                <div key={attribute.id} className={styles.tableRow}>
                  <div className={styles.colName}>
                    <span className={styles.attributeName}>{attribute.name}</span>
                  </div>
                  <div className={styles.colType}>
                    <span className={styles.badge}>{getTypeLabel(attribute.type)}</span>
                  </div>
                  <div className={styles.colCategory}>
                    <span className={`${styles.categoryBadge} ${styles[attribute.category || 'specification']}`}>
                      {attribute.category === 'variant' ? 'Variante' : 'Especificación'}
                    </span>
                  </div>
                  <div className={styles.colActions}>
                    <button 
                      className={styles.editButton} 
                      onClick={() => handleEditAttribute(attribute)}
                      disabled={loading}
                    >
                      Editar
                    </button>
                    <button 
                      className={styles.deleteButton} 
                      onClick={() => handleDeleteAttribute(attribute.id)}
                      disabled={loading}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <AttributeModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitForm}
        isLoading={loading}
        initialData={selectedAttribute}
        mode={modalMode}
      />
    </AdminLayout>
  );
};

export default AdminAttributes;
